import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useSubscriptionItems, so applying both narrows rather than
// resets.
//
// ⚠️ These filters are applied CLIENT-SIDE, over the pages loaded so far.
// `SubscriptionItemsFilterInput` declares exactly one field —
// `subscription: String!` — so the server filter IS the subscription scope and
// offers no field-level narrowing. See the comment in useSubscriptionItems.ts.
export type SubscriptionItemsFilters = Record<string, string>;

let globalFilters: SubscriptionItemsFilters = {};
const filterListeners: Set<(filters: SubscriptionItemsFilters) => void> = new Set();

const notifyFilterChange = (filters: SubscriptionItemsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetSubscriptionItemsFilter = () => notifyFilterChange({});

export const useSubscriptionItemsFilter = () => {
    const [filters, setFilters] = useState<SubscriptionItemsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: SubscriptionItemsFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
